/**
 * Lightweight self-hosted analytics tracker for PURO.
 * Privacy-friendly: no PII, no third party. Sends events to /api/analytics/event.
 *
 * Usage:
 *   import { initAnalytics, trackEvent } from '../lib/analytics';
 *   initAnalytics();                       // call once on app mount
 *   trackEvent('registration', { plan }); // custom events
 */
import axios from 'axios';
import { API } from '../config/api';

const SESSION_KEY = 'puro_session_id';
const SESSION_START_KEY = 'puro_session_start';
const LAST_PATH_KEY = 'puro_last_path';

function getOrCreateSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
      sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    }
    return sid;
  } catch {
    return null;
  }
}

async function send(eventType, extra = {}) {
  try {
    const session_id = getOrCreateSessionId();
    await axios.post(
      `${API}/analytics/event`,
      {
        type: eventType,
        path: window.location.pathname,
        referrer: document.referrer || null,
        session_id,
        ...extra,
      },
      { withCredentials: false }
    );
  } catch (e) {
    /* silent — analytics never blocks UX */
  }
}

export function trackEvent(eventType, meta = {}) {
  return send(eventType, { meta });
}

export function trackPageView() {
  // de-dupe consecutive identical paths
  try {
    const cur = window.location.pathname;
    if (sessionStorage.getItem(LAST_PATH_KEY) === cur) return;
    sessionStorage.setItem(LAST_PATH_KEY, cur);
  } catch {}
  return send('page_view');
}

export function initAnalytics() {
  // Initial page view
  getOrCreateSessionId();
  trackPageView();

  // SPA navigation: hook into history changes
  const fire = () => trackPageView();
  window.addEventListener('popstate', fire);
  // Patch pushState/replaceState
  const _push = window.history.pushState;
  const _replace = window.history.replaceState;
  window.history.pushState = function (...args) {
    _push.apply(this, args);
    setTimeout(fire, 0);
  };
  window.history.replaceState = function (...args) {
    _replace.apply(this, args);
    setTimeout(fire, 0);
  };

  // Session end on tab close — send duration
  const sendSessionEnd = () => {
    try {
      const start = parseInt(sessionStorage.getItem(SESSION_START_KEY) || '0', 10);
      if (!start) return;
      const duration_ms = Date.now() - start;
      const session_id = sessionStorage.getItem(SESSION_KEY);
      const url = `${API}/analytics/event`;
      const body = JSON.stringify({
        type: 'session_end',
        path: window.location.pathname,
        session_id,
        duration_ms,
      });
      // Use sendBeacon when possible for reliability on close
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
      }
    } catch {}
  };
  window.addEventListener('pagehide', sendSessionEnd);
  window.addEventListener('beforeunload', sendSessionEnd);
}
